// Brand profile page (private). LinkedIn-inspired identity + work experience.
// Brand-only; guarded. Uses BrandTopTabs.

import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig.js';
import { useAuth } from '../context/AuthContext.jsx';
import BrandTopTabs from '../components/brand/BrandTopTabs.jsx';
import OnboardingLoading from '../components/onboarding/OnboardingLoading.jsx';
import BrandIdentityEditModal from '../components/brand/BrandIdentityEditModal.jsx';
import BrandWorkExperienceModal from '../components/brand/BrandWorkExperienceModal.jsx';
import { formatExperienceDateRange, getWorkExperiencesForDisplay } from '../lib/brandWorkExperience';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const BrandProfile = ({ onNavigate }) => {
  const { user, profile, loading: authLoading } = useAuth();
  const [brandProfile, setBrandProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [identityModalOpen, setIdentityModalOpen] = useState(false);
  const [workModalOpen, setWorkModalOpen] = useState(false);
  const [saveNotice, setSaveNotice] = useState('');

  const role = profile?.role ?? profile?.userType;
  const isBrand = role === 'brand';

  useEffect(() => {
    if (!user?.uid || !isBrand) return;
    let cancelled = false;
    getDoc(doc(db, 'brandProfiles', user.uid))
      .then((snap) => {
        if (cancelled) return;
        setBrandProfile(snap.exists() ? snap.data() : null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.uid, isBrand]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      onNavigate('/login', null, { replace: true });
      return;
    }
    if (!isBrand && profile) {
      onNavigate('/profile/edit', null, { replace: true });
      return;
    }
  }, [user, profile, authLoading, isBrand, onNavigate]);

  const handleIdentitySaved = (merged) => {
    setBrandProfile(merged);
    setSaveNotice('Profile updated.');
    window.setTimeout(() => setSaveNotice(''), 4000);
  };

  const handleWorkSaved = (merged) => {
    setBrandProfile(merged);
    setSaveNotice('Work experience updated.');
    window.setTimeout(() => setSaveNotice(''), 4000);
  };

  if (authLoading || (user && !profile && !isBrand)) {
    return (
      <OnboardingLoading
        label="Loading"
        sublabel="Loading your profile."
        progressPercent={50}
      />
    );
  }
  if (!user || !isBrand) return null;

  const contact = brandProfile?.contact || {};
  const company = brandProfile?.company || {};
  const media = brandProfile?.media || {};
  const workExperiences = getWorkExperiencesForDisplay(brandProfile);

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandTopTabs onNavigate={onNavigate} activeTab="profile" brandProfile={brandProfile} />

      <main className="max-w-5xl mx-auto px-4 py-6 sm:px-6">
        <div className="mb-2">
          <h1 className="text-xl font-semibold text-slate-900">Profile</h1>
          <p className="text-sm text-slate-500">This profile will be available for influencers to view for outreach.</p>
        </div>

        {saveNotice ? (
          <div
            className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
            role="status"
          >
            {saveNotice}
          </div>
        ) : null}

        {loading ? (
          <p className="text-slate-600">Loading profile...</p>
        ) : (
          <>
            {/* Identity card - layout aligned with influencer IdentitySection (banner, overlapping avatar, content below) */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm mb-6 relative">
              {/* A) Banner - same aspect/height as influencer profile */}
              <div className="relative w-full">
                <div className="relative w-full aspect-[4/1] max-h-[112px] sm:max-h-[140px] overflow-hidden rounded-t-xl bg-gradient-to-r from-[#0ea5e9] to-[#1B9DA2]" />

                {/* B) Avatar - overlaps bottom of banner, same sizing as IdentitySection */}
                <div className="absolute left-4 sm:left-8 -bottom-10 sm:-bottom-12 z-10">
                  <div className="h-[100px] w-[100px] sm:h-[120px] sm:w-[120px] rounded-full bg-white border-4 border-white shadow-md overflow-hidden flex items-center justify-center">
                    {media.personAvatarUrl ? (
                      <img src={media.personAvatarUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-slate-400 text-3xl font-medium">
                        {(contact.firstName || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* C) Content block - below banner/avatar, same padding as IdentitySection */}
              <div className="pt-12 sm:pt-14 pb-4 sm:pb-6 px-4 sm:px-6 relative">
                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
                      {contact.firstName || contact.lastName
                        ? [contact.firstName, contact.lastName].filter(Boolean).join(' ')
                        : 'Brand user'}
                      {company.name ? ` at ${company.name}` : ''}
                    </h2>
                    {company.roleTitle && (
                      <p className="text-sm text-slate-600 mt-0.5">{company.roleTitle}</p>
                    )}
                    {contact.headline ? (
                      <p className="text-sm text-slate-600 mt-0.5">{contact.headline}</p>
                    ) : (
                      <p className="text-sm text-slate-500 mt-2">
                        Add a headline so creators understand what you are looking for. Use{' '}
                        <button
                          type="button"
                          onClick={() => setIdentityModalOpen(true)}
                          className="font-medium text-[#1B9DA2] hover:underline"
                        >
                          Edit profile
                        </button>
                        .
                      </p>
                    )}

                    {/* Meta row: Website, LinkedIn, Email */}
                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      {company.website ? (
                        <a
                          href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1B9DA2] hover:underline"
                        >
                          Website
                        </a>
                      ) : (
                        <span className="text-slate-500">Add your company website in Edit profile.</span>
                      )}
                      {contact.linkedInUrl && (
                        <a
                          href={contact.linkedInUrl.startsWith('http') ? contact.linkedInUrl : `https://${contact.linkedInUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1B9DA2] hover:underline"
                        >
                          LinkedIn
                        </a>
                      )}
                      {contact.email && (
                        <span className="text-slate-600">
                          Email: {contact.email}
                        </span>
                      )}
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => setIdentityModalOpen(true)}
                        className="rounded-lg bg-[#1B9DA2] px-4 py-2 text-sm font-medium text-white hover:bg-[#168589]"
                      >
                        Edit profile
                      </button>
                    </div>
                  </div>
                  {company.logoUrl && (
                    <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                      <img src={company.logoUrl} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Work experience */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm relative">
              <div className="flex items-start justify-between gap-3 pr-0">
                <h3 className="text-sm font-semibold text-slate-900">Work experience</h3>
                <button
                  type="button"
                  onClick={() => setWorkModalOpen(true)}
                  className="flex-shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300"
                >
                  Edit
                </button>
              </div>
              {workExperiences.length > 0 ? (
                <ul className="mt-4 space-y-4">
                  {workExperiences.map((exp) => (
                    <li
                      key={exp.id}
                      className="flex gap-3 border-t border-slate-100 pt-4 first:border-t-0 first:pt-0"
                    >
                      {exp.logoUrl ? (
                        <div className="w-12 h-12 rounded-lg border border-slate-200 overflow-hidden bg-slate-50 flex-shrink-0">
                          <img src={exp.logoUrl} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-lg border border-dashed border-slate-200 bg-slate-50 flex-shrink-0" aria-hidden />
                      )}
                      <div className="min-w-0">
                        {exp.companyName ? (
                          <p className="font-medium text-slate-900">{exp.companyName}</p>
                        ) : null}
                        {exp.roleTitle ? <p className="text-sm text-slate-600">{exp.roleTitle}</p> : null}
                        <p className="text-xs text-slate-500 mt-0.5">
                          {formatExperienceDateRange(exp, MONTH_NAMES)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-slate-500">
                  No work experience yet. Click <span className="font-medium">Edit</span> to add roles.
                </p>
              )}
            </div>
          </>
        )}
      </main>

      {user?.uid && (
        <>
          <BrandIdentityEditModal
            isOpen={identityModalOpen}
            onClose={() => setIdentityModalOpen(false)}
            userId={user.uid}
            brandProfile={brandProfile}
            onSaved={handleIdentitySaved}
          />
          <BrandWorkExperienceModal
            isOpen={workModalOpen}
            onClose={() => setWorkModalOpen(false)}
            userId={user.uid}
            brandProfile={brandProfile}
            onSaved={handleWorkSaved}
          />
        </>
      )}
    </div>
  );
};

export default BrandProfile;
